import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import { useFormSubmit } from '../../hooks/useFormSubmit';

const Newsletter = () => {
  const [email, setEmail] = useState('');
  const [preferences, setPreferences] = useState({ tools: true, strategy: true, ai: false });
  const { status, error, submitForm, resetFormStatus } = useFormSubmit();

  const handleSubscribe = async (e) => {
    e.preventDefault();

    const payload = {
      email,
      preferences,
      listName: 'Newsletter'
    };

    const success = await submitForm(payload);
    if (success) {
      setEmail('');
      setPreferences({ tools: true, strategy: true, ai: false });
    }
  };

  return (
    <section className="py-24 bg-brand-dark border-y border-brand-red relative overflow-hidden">
      {/* Faded Grid with White lines in Dark Mode */}
      <div 
        className="absolute inset-0 opacity-[0.08] dark:opacity-[0.12] pointer-events-none" 
        style={{
          backgroundImage: 'linear-gradient(to right, rgb(var(--grid-color)) 1px, transparent 1px), linear-gradient(to bottom, rgb(var(--grid-color)) 1px, transparent 1px)', 
          backgroundSize: '40px 40px',
          maskImage: 'radial-gradient(circle at center, transparent 0%, black 80%)',
          WebkitMaskImage: 'radial-gradient(circle at center, transparent 0%, black 80%)'
        }} 
      ></div>
      
      <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <span className="text-xs uppercase tracking-[0.3em] text-brand-cyan font-bold mb-4 block">Enhanced Insights</span>
          <h2 className="text-3xl md:text-5xl font-heading font-bold text-white mb-6">
            Escape <span className="text-brand-yellow">Tool Chaos</span>
          </h2>
          <p className="text-slate-300 mb-10 max-w-2xl mx-auto font-light text-lg">
            Customize your briefing. Select the strategic domains you want to master and receive highly curated, actionable intelligence.
          </p>

          {status === 'success' ? (
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-slate-900/80 backdrop-blur p-8 border-2 border-brand-cyan inline-block rounded-xl"
            >
              <div className="flex flex-col items-center gap-4 text-white">
                <SafeIcon name="CheckCircle" className="w-12 h-12 text-brand-cyan" />
                <span className="font-heading text-xl font-bold tracking-wide">Briefing Preferences Saved.</span>
                <p className="text-slate-400 text-sm">Welcome to strategic clarity.</p>
                <button
                  onClick={resetFormStatus}
                  className="mt-4 px-6 py-2 bg-slate-100 dark:bg-slate-800 text-brand-dark dark:text-white text-xs uppercase font-bold tracking-widest rounded hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                >
                  Manage Preferences
                </button>
              </div>
            </motion.div>
          ) : (
              <form onSubmit={handleSubscribe} className="max-w-xl mx-auto bg-slate-900/80 backdrop-blur p-8 border border-white/10 rounded-xl shadow-2xl">

              {status === 'error' && (
                <div className="mb-6 p-4 bg-red-500/10 border border-red-500/50 rounded-lg text-red-500 text-sm font-medium">
                  {error || 'Failed to submit. Please try again.'}
                </div>
              )}

              <div className="mb-8 text-left">
                <label className="block text-xs uppercase tracking-widest text-brand-yellow font-bold mb-4">Select Strategic Focus Areas:</label>
                <div className="space-y-3">
                  <label className="flex items-center gap-3 cursor-pointer group">
                      <input type="checkbox" name="tools" className="hidden" checked={preferences.tools} onChange={() => setPreferences(p => ({ ...p, tools: !p.tools }))} />
                    <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${preferences.tools ? 'bg-brand-cyan border-brand-cyan text-brand-dark' : 'border-slate-500 group-hover:border-brand-cyan'}`}>
                      {preferences.tools && <SafeIcon name="Check" className="w-3 h-3" />}
                    </div>
                    <span className="text-slate-200 text-sm font-medium">Marketing Tech & Tool Audits</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer group">
                      <input type="checkbox" name="strategy" className="hidden" checked={preferences.strategy} onChange={() => setPreferences(p => ({ ...p, strategy: !p.strategy }))} />
                    <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${preferences.strategy ? 'bg-brand-cyan border-brand-cyan text-brand-dark' : 'border-slate-500 group-hover:border-brand-cyan'}`}>
                      {preferences.strategy && <SafeIcon name="Check" className="w-3 h-3" />}
                    </div>
                    <span className="text-slate-200 text-sm font-medium">High-Level Brand Strategy</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer group">
                      <input type="checkbox" name="ai" className="hidden" checked={preferences.ai} onChange={() => setPreferences(p => ({ ...p, ai: !p.ai }))} />
                    <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${preferences.ai ? 'bg-brand-cyan border-brand-cyan text-brand-dark' : 'border-slate-500 group-hover:border-brand-cyan'}`}>
                      {preferences.ai && <SafeIcon name="Check" className="w-3 h-3" />}
                    </div>
                    <span className="text-slate-200 text-sm font-medium">AI Orchestration Workflows</span>
                  </label>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <input 
                    name="email"
                  type="email" 
                  required
                  placeholder="Executive Email Address" 
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-grow px-6 py-4 bg-brand-dark border-2 border-slate-700 focus:border-brand-cyan outline-none transition-colors text-white rounded-lg"
                />
                <button 
                  type="submit" 
                  disabled={status === 'loading'}
                  className="px-8 py-4 bg-brand-red text-white text-sm uppercase tracking-widest font-bold hover:bg-red-600 transition-colors rounded-lg disabled:opacity-50"
                >
                  {status === 'loading' ? 'Processing...' : 'Subscribe'}
                </button>
              </div>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default Newsletter;